<div class="credentials-container">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <h2>Credentials</h2>
      <p class="section-description">Manage API keys and authentication credentials for your integrations</p>
    </div>
    <!-- Only show Add button if we have existing credentials (empty state has its own button) -->
    <button class="btn btn-primary" (click)="startCreate()" [disabled]="isCreating || isSaving || !selectedOrganization" *ngIf="hasCredentials">
      <i class="fas fa-plus"></i> Add Credential
    </button>
  </div>

  <!-- Messages -->
  <div class="message error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>
  <div class="message success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <i class="fas fa-spinner fa-spin"></i> Loading credentials...
  </div>

  <!-- Search -->
  <div class="filters-row" *ngIf="!isLoading && selectedOrganization">
    <div class="search-box">
      <i class="fas fa-search"></i>
      <input type="text" [(ngModel)]="searchQuery" placeholder="Search credentials..." />
    </div>
  </div>

  <!-- Create Form -->
  <div class="create-form card" *ngIf="isCreating">
    <h3>New Credential</h3>
    <div class="form-grid">
      <div class="form-group">
        <label>Name *</label>
        <input type="text" [(ngModel)]="createForm.name" placeholder="e.g., HubSpot Production API Key" />
      </div>
      <div class="form-group">
        <label>Type *</label>
        <select [(ngModel)]="createForm.credentialTypeId" (change)="onTypeChange(createForm.credentialTypeId)">
          <option value="">Select a type...</option>
          <option *ngFor="let type of credentialTypes" [value]="type.ID">{{ type.Name }}</option>
        </select>
      </div>
      <div class="form-group">
        <label>Expires On</label>
        <input type="date" [(ngModel)]="createForm.expiresAt" />
        <small class="help-text">Leave blank if the credential doesn't expire</small>
      </div>
      <div class="form-group full-width">
        <label>Description</label>
        <textarea [(ngModel)]="createForm.description" placeholder="Optional description..." rows="2"></textarea>
      </div>
    </div>

    <!-- AI Provider Tier Guidance (only for AI category credentials) -->
    <div class="ai-tier-guidance" *ngIf="isAICredentialType(createForm.credentialTypeId)">
      <div class="ai-tier-header">
        <i class="fas fa-triangle-exclamation"></i>
        <span>AI provider tier requirements</span>
      </div>
      <p class="ai-tier-lead">
        Izzy uses <strong>300,000&ndash;400,000 tokens per message</strong>. Your API key must be on a
        tier with enough rate limit headroom, or messages will fail with rate-limit or quota errors.
      </p>
      <ul class="ai-tier-list">
        <li>
          <strong>OpenAI:</strong> Tier&nbsp;3 or higher recommended ($100 paid, 7-day wait).
          <a href="https://platform.openai.com/docs/guides/rate-limits" target="_blank" rel="noopener">View tiers</a>
        </li>
        <li>
          <strong>Anthropic:</strong> Tier&nbsp;2 or higher recommended ($40 paid, 7-day wait).
          <a href="https://docs.anthropic.com/en/api/rate-limits" target="_blank" rel="noopener">View tiers</a>
        </li>
        <li>
          <strong>Google Gemini:</strong> Tier&nbsp;1 or higher recommended ($250 cumulative spend).
          <a href="https://ai.google.dev/gemini-api/docs/rate-limits" target="_blank" rel="noopener">View tiers</a>
        </li>
      </ul>
      <p class="ai-tier-footnote">
        Free-tier and lowest-tier keys will throttle under normal Izzy load. Tier thresholds change over time &mdash; verify current limits on the provider&rsquo;s page.
      </p>
    </div>

    <!-- Dynamic Credential Fields Based on Type -->
    <div class="credential-fields" *ngIf="createForm.credentialTypeId && schemaFields.length > 0">
      <h4>Credential Details</h4>
      <p class="security-note">
        <i class="fas fa-shield-alt"></i>
        These values will be encrypted. You won't be able to view them after saving.
      </p>
      <div class="form-grid">
        <div class="form-group" *ngFor="let field of schemaFields">
          <label>{{ field.label }} <span *ngIf="field.required">*</span></label>
          <input
            [type]="field.type"
            [(ngModel)]="createForm.credentialData[field.name]"
            [placeholder]="field.help || ''"
          />
          <small class="help-text" *ngIf="field.help">{{ field.help }}</small>
        </div>
      </div>
    </div>

    <div class="form-group full-width" *ngIf="createForm.credentialTypeId">
      <label>Notes</label>
      <textarea [(ngModel)]="createForm.description" placeholder="Internal notes about this credential..." rows="2"></textarea>
    </div>

    <div class="form-actions">
      <button class="btn btn-secondary" (click)="cancelCreate()" [disabled]="isSaving">Cancel</button>
      <button class="btn btn-primary" (click)="saveCreate()" [disabled]="isSaving">
        <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
        {{ isSaving ? 'Saving...' : 'Create Credential' }}
      </button>
    </div>
  </div>

  <!-- Credentials List -->
  <div class="credentials-grid" *ngIf="!isLoading && selectedOrganization">
    <div class="credential-card card" *ngFor="let cd of filteredCredentials" [class.editing]="cd.isEditing">
      <!-- View Mode -->
      <ng-container *ngIf="!cd.isEditing">
        <div class="credential-header">
          <div class="credential-icon" [class.expiring]="isExpiringSoon(cd.credential)" [class.expired]="isExpired(cd.credential)">
            <i class="fab" [ngClass]="getTypeIcon(cd.credentialType?.Name || '')"></i>
          </div>
          <div class="credential-info">
            <h4>{{ displayName(cd.credential.Name) }}</h4>
            <div class="credential-meta">
              <span class="type-badge">{{ cd.credentialType?.Name || 'Unknown Type' }}</span>
              <span class="status-badge" [ngClass]="getStatusClass(getCredentialStatusLabel(cd.credential))">{{ getCredentialStatusLabel(cd.credential) }}</span>
            </div>
            <p class="credential-description" *ngIf="cd.credential.Description">{{ cd.credential.Description }}</p>
          </div>
        </div>
        <div class="credential-footer">
          <div class="credential-dates">
            <span *ngIf="cd.credential.LastUsedAt" class="last-used" title="Last used">
              <i class="fas fa-clock"></i> {{ cd.credential.LastUsedAt | date:'short' }}
            </span>
            <span *ngIf="cd.credential.ExpiresAt" class="expires" [class.warning]="isExpiringSoon(cd.credential)" [class.danger]="isExpired(cd.credential)">
              <i class="fas fa-calendar-times"></i>
              {{ isExpired(cd.credential) ? 'Expired' : 'Expires' }} {{ cd.credential.ExpiresAt | date:'mediumDate' }}
            </span>
          </div>
          <div class="credential-actions">
            <button class="btn btn-icon" title="Edit" (click)="startEdit(cd)" [disabled]="isSaving">
              <i class="fas fa-edit"></i>
            </button>
            <button class="btn btn-icon btn-danger" title="Delete" (click)="confirmDelete(cd)" [disabled]="isSaving">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </ng-container>

      <!-- Edit Mode -->
      <ng-container *ngIf="cd.isEditing">
        <div class="edit-form">
          <h4>Edit Credential</h4>
          <div class="form-group">
            <label>Name</label>
            <input type="text" [(ngModel)]="cd.editForm.name" />
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea [(ngModel)]="cd.editForm.description" rows="2"></textarea>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Status</label>
              <select [(ngModel)]="cd.editForm.status">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div class="form-group">
              <label>Expires On</label>
              <input type="date" [(ngModel)]="cd.editForm.expiresAt" />
            </div>
          </div>
          <div class="form-group">
            <label>Notes</label>
            <textarea [(ngModel)]="cd.editForm.description" rows="2"></textarea>
          </div>

          <!-- Update Credential Values -->
          <div class="credential-fields" *ngIf="cd.editSchemaFields.length > 0">
            <h4>Update Credential Values</h4>
            <p class="security-note">
              <i class="fas fa-shield-alt"></i>
              Leave fields blank to keep existing values. New values will be encrypted.
            </p>
            <div class="form-grid">
              <div class="form-group" *ngFor="let field of cd.editSchemaFields">
                <label>{{ field.label }}</label>
                <input
                  [type]="field.type"
                  [(ngModel)]="cd.editForm.credentialData[field.name]"
                  [placeholder]="'Enter new value to update'"
                />
                <small class="help-text" *ngIf="field.help">{{ field.help }}</small>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button class="btn btn-secondary" (click)="cancelEdit(cd)" [disabled]="isSaving">Cancel</button>
            <button class="btn btn-primary" (click)="saveEdit(cd)" [disabled]="isSaving">
              <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
              Save
            </button>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Empty State -->
    <div class="empty-state card" *ngIf="filteredCredentials.length === 0 && !isCreating">
      <i class="fas fa-lock"></i>
      <p>No credentials configured</p>
      <p class="empty-hint">Add credentials to connect to external services like HubSpot, Twilio, or Microsoft Graph</p>
      <button class="btn btn-primary" (click)="startCreate()">Add Your First Credential</button>
    </div>
  </div>

  <!-- Confirm Dialog -->
  <div class="modal-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDialogYes()" [disabled]="isSaving">
          <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
          Delete
        </button>
      </div>
    </div>
  </div>
</div>
